import { AxiosRequestConfig, AxiosResponse } from 'axios';

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplicationService {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private serialQueues: Map<string, Promise<void>> = new Map();
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  /**
   * Generate a unique key for the request based on URL, method, and parameters
   */
  private generateRequestKey(config: AxiosRequestConfig): string {
    const { url, method = 'GET', params, data } = config;
    
    // Create a stable key from request details
    const keyData = {
      url,
      method: method.toUpperCase(),
      params: params || {},
      data: data || {}
    };
    
    return JSON.stringify(keyData);
  }

  /**
   * Clean up expired requests
   */
  private cleanupExpiredRequests(): void {
    const now = Date.now();
    Array.from(this.pendingRequests.entries()).forEach(([key, request]) => {
      if (now - request.timestamp > this.REQUEST_TIMEOUT) {
        this.pendingRequests.delete(key);
      }
    });
  }

  /**
   * Execute request with deduplication
   */
  public async executeRequest<T = any>(
    config: AxiosRequestConfig,
    requestFunction: () => Promise<T>
  ): Promise<T> {
    // Clean up expired requests periodically
    this.cleanupExpiredRequests();

    const requestKey = this.generateRequestKey(config);
    
    // Check if identical request is already in progress
    const existingRequest = this.pendingRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest.promise;
    }

    // Execute the request
    const promise = requestFunction()
      .then((response) => {
        // Clean up completed request
        this.pendingRequests.delete(requestKey);
        return response;
      })
      .catch((error) => {
        // Clean up failed request
        this.pendingRequests.delete(requestKey);
        throw error;
      });

    // Store the pending request
    this.pendingRequests.set(requestKey, {
      promise,
      timestamp: Date.now()
    });

    return promise;
  }

  /**
   * Run one at a time against a key, rather than collapsing identical calls the way executeRequest
   * does - two saves carrying different payloads are not duplicates, so that would let both through.
   * A plot save replaces its stored tooltip rows wholesale, which leaves an overlapping second save
   * acting on rows the first has already replaced.
   */
  public executeSerial<T>(key: string, requestFunction: () => Promise<T>): Promise<T> {
    const previous = this.serialQueues.get(key) ?? Promise.resolve();
    const result = previous.then(requestFunction);

    // Queued on the swallowed promise, so a rejected save does not stall everything behind it.
    const settled = result.then(() => undefined, () => undefined);

    this.serialQueues.set(key, settled);
    settled.then(() => {
      if (this.serialQueues.get(key) === settled) {
        this.serialQueues.delete(key);
      }
    });

    return result;
  }

  /**
   * Clear all pending requests (useful for component unmount)
   */
  public clearAll(): void {
    this.pendingRequests.clear();
    this.serialQueues.clear();
  }

  /**
   * Clear specific request by key
   */
  public clearRequest(config: AxiosRequestConfig): void {
    const requestKey = this.generateRequestKey(config);
    this.pendingRequests.delete(requestKey);
  }

  /**
   * Get number of pending requests (for debugging)
   */
  public getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Debug method to log all pending requests
   */
  public debugPendingRequests(): void {
    Array.from(this.pendingRequests.entries()).forEach(([key, request]) => {
      console.log(`⏳ Pending: ${key} (${Date.now() - request.timestamp}ms ago)`);
    });
  }
}

// Export singleton instance
export const requestDeduplicationService = new RequestDeduplicationService();

// Export hook for React components
export const useRequestDeduplication = () => {
  return {
    executeRequest: requestDeduplicationService.executeRequest.bind(requestDeduplicationService),
    clearAll: requestDeduplicationService.clearAll.bind(requestDeduplicationService),
    clearRequest: requestDeduplicationService.clearRequest.bind(requestDeduplicationService),
    getPendingRequestsCount: requestDeduplicationService.getPendingRequestsCount.bind(requestDeduplicationService),
    debugPendingRequests: requestDeduplicationService.debugPendingRequests.bind(requestDeduplicationService)
  };
}; 